/**
 * Утилита для получения иконок и цветов системных категорий
 * Использует пастельные цвета и иконки MaterialCommunityIcons
 */

import { getCustomIconByName } from './customCategoryIcons';

export interface CategoryIconConfig {
  icon: string;
  color: string;
  backgroundColor: string; // Пастельный фон для иконки
}

// Пастельные цвета для категорий
const PASTEL_COLORS = {
  // Продукты - пастельный оранжевый
  products: { color: '#FF9F66', bg: '#FFF4ED' },
  // Транспорт - пастельный синий
  transport: { color: '#7B9CF5', bg: '#F0F4FF' },
  // ЖКХ - пастельный зеленый
  utilities: { color: '#6BCB9A', bg: '#F0FDF4' },
  // Кафе и рестораны - пастельный красный
  cafe: { color: '#FF8A95', bg: '#FFF1F2' },
  // Одежда - пастельный розовый
  clothing: { color: '#FFB3D1', bg: '#FFF0F8' },
  // Здоровье - пастельный красный (более мягкий)
  health: { color: '#FF9AA2', bg: '#FFF0F1' },
  // Развлечения - пастельный фиолетовый
  entertainment: { color: '#C8A8E9', bg: '#F9F5FF' },
  // Подписки - пастельный синий (другой оттенок)
  subscriptions: { color: '#8BB3F2', bg: '#F0F7FF' },
  // Подарки - пастельный розовый (другой оттенок)
  gifts: { color: '#FFB8D9', bg: '#FFF0F8' },
  // Личное - пастельный персиковый
  personal: { color: '#FFC5A1', bg: '#FFF8F3' },
  // Дом - пастельный коричневый/бежевый
  home: { color: '#D4A574', bg: '#FDF8F3' },
  // Образование - пастельный синий (учебный)
  education: { color: '#7BC8F6', bg: '#F0F9FF' },
  // Питомцы - пастельный коричневый
  pets: { color: '#D4A574', bg: '#FDF8F3' },
  // Связь - пастельный голубой
  communication: { color: '#7BC8F6', bg: '#F0F9FF' },
  // Финансы - пастельный зеленый (деньги)
  finance: { color: '#6BCB9A', bg: '#F0FDF4' },
  // Путешествия - пастельный желтый
  travel: { color: '#FFD93D', bg: '#FFFEF0' },
  // Дети - пастельный розовый (детский)
  children: { color: '#FFB3D1', bg: '#FFF0F8' },
  // Благотворительность - пастельный красный (сердечный)
  charity: { color: '#FF9AA2', bg: '#FFF0F1' },
  rent: { color: '#7F8C8D', bg: '#F4F6F7' },
  transfers: { color: '#58D68D', bg: '#EAFAF1' },
  sports: { color: '#5DADE2', bg: '#EBF8FF' },
  fines: { color: '#E67E22', bg: '#FDF2E9' },
  alcohol: { color: '#D68910', bg: '#FEF5E7' },
  beauty: { color: '#E6A8D7', bg: '#FDF2FA' },
  interest: { color: '#34495E', bg: '#EBF0F5' },
  refund: { color: '#E67E22', bg: '#FDF2E9' },
};

/** Явные иконки для Plaid категорий и подкатегорий */
const PLAID_CATEGORY_ICONS: Record<string, { icon: string; color: string; bg: string }> = {
  'еда и напитки': { icon: 'coffee', color: PASTEL_COLORS.cafe.color, bg: PASTEL_COLORS.cafe.bg },
  'аренда и жкх': { icon: 'home', color: PASTEL_COLORS.utilities.color, bg: PASTEL_COLORS.utilities.bg },
  'покупки': { icon: 'shopping-cart', color: PASTEL_COLORS.products.color, bg: PASTEL_COLORS.products.bg },
  'услуги': { icon: 'briefcase', color: PASTEL_COLORS.transport.color, bg: PASTEL_COLORS.transport.bg },
  'кредиты': { icon: 'credit-card', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'банковские комиссии': { icon: 'building', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'госуслуги': { icon: 'target', color: PASTEL_COLORS.rent.color, bg: PASTEL_COLORS.rent.bg },
  'входящие переводы': { icon: 'arrow-down', color: PASTEL_COLORS.transfers.color, bg: PASTEL_COLORS.transfers.bg },
  'исходящие переводы': { icon: 'arrow-up', color: PASTEL_COLORS.transfers.color, bg: PASTEL_COLORS.transfers.bg },
  'доход': { icon: 'dollar-sign', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'овердрафт': { icon: 'alert-circle', color: PASTEL_COLORS.fines.color, bg: PASTEL_COLORS.fines.bg },
  'банкомат': { icon: 'dollar-sign', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'просрочка': { icon: 'clock', color: PASTEL_COLORS.fines.color, bg: PASTEL_COLORS.fines.bg },
  'спор': { icon: 'alert-triangle', color: PASTEL_COLORS.fines.color, bg: PASTEL_COLORS.fines.bg },
  'зарубежная операция': { icon: 'globe', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'недостаточно средств': { icon: 'wallet', color: PASTEL_COLORS.fines.color, bg: PASTEL_COLORS.fines.bg },
  'наличные': { icon: 'dollar-sign', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'превышение лимита': { icon: 'bar-chart', color: PASTEL_COLORS.fines.color, bg: PASTEL_COLORS.fines.bg },
  'приюты': { icon: 'star', color: PASTEL_COLORS.pets.color, bg: PASTEL_COLORS.pets.bg },
  'пансионаты': { icon: 'building', color: PASTEL_COLORS.health.color, bg: PASTEL_COLORS.health.bg },
  'госорганы': { icon: 'building', color: PASTEL_COLORS.rent.color, bg: PASTEL_COLORS.rent.bg },
  'правоохранительные органы': { icon: 'shield', color: PASTEL_COLORS.rent.color, bg: PASTEL_COLORS.rent.bg },
  'библиотеки': { icon: 'book', color: PASTEL_COLORS.education.color, bg: PASTEL_COLORS.education.bg },
  'военные': { icon: 'award', color: PASTEL_COLORS.rent.color, bg: PASTEL_COLORS.rent.bg },
  'организации': { icon: 'users', color: PASTEL_COLORS.rent.color, bg: PASTEL_COLORS.rent.bg },
  'почта': { icon: 'mail', color: PASTEL_COLORS.communication.color, bg: PASTEL_COLORS.communication.bg },
  'соцуслуги': { icon: 'heart', color: PASTEL_COLORS.charity.color, bg: PASTEL_COLORS.charity.bg },
  'религия': { icon: 'gift', color: PASTEL_COLORS.charity.color, bg: PASTEL_COLORS.charity.bg },
  'для пожилых': { icon: 'user', color: PASTEL_COLORS.health.color, bg: PASTEL_COLORS.health.bg },
  'бары': { icon: 'coffee', color: PASTEL_COLORS.alcohol.color, bg: PASTEL_COLORS.alcohol.bg },
  'пивоварни': { icon: 'music', color: PASTEL_COLORS.alcohol.color, bg: PASTEL_COLORS.alcohol.bg },
  'интернет-кафе': { icon: 'laptop', color: PASTEL_COLORS.cafe.color, bg: PASTEL_COLORS.cafe.bg },
  'ночная жизнь': { icon: 'music', color: PASTEL_COLORS.entertainment.color, bg: PASTEL_COLORS.entertainment.bg },
  'медуслуги': { icon: 'heart', color: PASTEL_COLORS.health.color, bg: PASTEL_COLORS.health.bg },
  'врачи': { icon: 'activity', color: PASTEL_COLORS.health.color, bg: PASTEL_COLORS.health.bg },
  'начисленные проценты': { icon: 'trending-up', color: PASTEL_COLORS.interest.color, bg: PASTEL_COLORS.interest.bg },
  'уплаченные проценты': { icon: 'trending-down', color: PASTEL_COLORS.fines.color, bg: PASTEL_COLORS.fines.bg },
  'кредитная карта': { icon: 'credit-card', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'искусство и развлечения': { icon: 'gamepad', color: PASTEL_COLORS.entertainment.color, bg: PASTEL_COLORS.entertainment.bg },
  'спортзалы': { icon: 'activity', color: PASTEL_COLORS.sports.color, bg: PASTEL_COLORS.sports.bg },
  'парки': { icon: 'sun', color: PASTEL_COLORS.sports.color, bg: PASTEL_COLORS.sports.bg },
  'спортивные лагеря': { icon: 'target', color: PASTEL_COLORS.sports.color, bg: PASTEL_COLORS.sports.bg },
  'автосервис': { icon: 'wrench', color: PASTEL_COLORS.transport.color, bg: PASTEL_COLORS.transport.bg },
  'кабельное тв': { icon: 'tv', color: PASTEL_COLORS.subscriptions.color, bg: PASTEL_COLORS.subscriptions.bg },
  'уборка': { icon: 'settings', color: PASTEL_COLORS.home.color, bg: PASTEL_COLORS.home.bg },
  'финансовые услуги': { icon: 'bar-chart', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'ремонт дома': { icon: 'construction', color: PASTEL_COLORS.home.color, bg: PASTEL_COLORS.home.bg },
  'страхование': { icon: 'shield', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'интернет': { icon: 'wifi', color: PASTEL_COLORS.communication.color, bg: PASTEL_COLORS.communication.bg },
  'недвижимость': { icon: 'home', color: PASTEL_COLORS.rent.color, bg: PASTEL_COLORS.rent.bg },
  'ветеринары': { icon: 'star', color: PASTEL_COLORS.pets.color, bg: PASTEL_COLORS.pets.bg },
  'косметика': { icon: 'star', color: PASTEL_COLORS.beauty.color, bg: PASTEL_COLORS.beauty.bg },
  'магазины у дома': { icon: 'shopping-bag', color: PASTEL_COLORS.products.color, bg: PASTEL_COLORS.products.bg },
  'универмаги': { icon: 'shopping-bag', color: PASTEL_COLORS.products.color, bg: PASTEL_COLORS.products.bg },
  'мебель': { icon: 'home', color: PASTEL_COLORS.home.color, bg: PASTEL_COLORS.home.bg },
  'хозтовары': { icon: 'construction', color: PASTEL_COLORS.home.color, bg: PASTEL_COLORS.home.bg },
  'спорттовары': { icon: 'activity', color: PASTEL_COLORS.sports.color, bg: PASTEL_COLORS.sports.bg },
  'супермаркеты': { icon: 'shopping-cart', color: PASTEL_COLORS.products.color, bg: PASTEL_COLORS.products.bg },
  'налоговый возврат': { icon: 'trending-up', color: PASTEL_COLORS.refund.color, bg: PASTEL_COLORS.refund.bg },
  'налог': { icon: 'file-text', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'вклад': { icon: 'dollar-sign', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'снятие': { icon: 'wallet', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'платёжные сервисы': { icon: 'smartphone', color: PASTEL_COLORS.finance.color, bg: PASTEL_COLORS.finance.bg },
  'авиа': { icon: 'plane', color: PASTEL_COLORS.travel.color, bg: PASTEL_COLORS.travel.bg },
  'аэропорты': { icon: 'map-pin', color: PASTEL_COLORS.travel.color, bg: PASTEL_COLORS.travel.bg },
  'аренда авто': { icon: 'car', color: PASTEL_COLORS.transport.color, bg: PASTEL_COLORS.transport.bg },
  'азс': { icon: 'zap', color: PASTEL_COLORS.transport.color, bg: PASTEL_COLORS.transport.bg },
  'дороги': { icon: 'map-pin', color: PASTEL_COLORS.transport.color, bg: PASTEL_COLORS.transport.bg },
  'парковка': { icon: 'target', color: PASTEL_COLORS.transport.color, bg: PASTEL_COLORS.transport.bg },
  'такси': { icon: 'car', color: PASTEL_COLORS.transport.color, bg: PASTEL_COLORS.transport.bg },
  'общественный транспорт': { icon: 'car', color: PASTEL_COLORS.transport.color, bg: PASTEL_COLORS.transport.bg },
  'отели': { icon: 'home', color: PASTEL_COLORS.travel.color, bg: PASTEL_COLORS.travel.bg },
  'прочий транспорт': { icon: 'plane', color: PASTEL_COLORS.transport.color, bg: PASTEL_COLORS.transport.bg },
  'плата за жильё': { icon: 'home', color: PASTEL_COLORS.rent.color, bg: PASTEL_COLORS.rent.bg },
  'интернет и тв': { icon: 'monitor', color: PASTEL_COLORS.communication.color, bg: PASTEL_COLORS.communication.bg },
  'телефон': { icon: 'phone', color: PASTEL_COLORS.communication.color, bg: PASTEL_COLORS.communication.bg },
  'газ и электричество': { icon: 'zap', color: PASTEL_COLORS.utilities.color, bg: PASTEL_COLORS.utilities.bg },
  'вода': { icon: 'droplet', color: PASTEL_COLORS.utilities.color, bg: PASTEL_COLORS.utilities.bg },
  'канализация и вывоз мусора': { icon: 'trash-2', color: PASTEL_COLORS.utilities.color, bg: PASTEL_COLORS.utilities.bg },
  'прочие жку': { icon: 'settings', color: PASTEL_COLORS.utilities.color, bg: PASTEL_COLORS.utilities.bg },
};

// Маппинг MaterialCommunityIcons имен на веб-иконки
const ICON_MAPPING: Record<string, string> = {
  'plus-circle': 'add',
  'heart': 'heart',
  'baby-face-outline': 'user',
  'home-lightning-bolt': 'home',
  'heart-pulse': 'activity',
  'silverware-fork-knife': 'coffee',
  'account': 'user',
  'school': 'book',
  'tshirt-crew': 'shopping-bag',
  'parking': 'car',
  'paw': 'star',
  'credit-card-multiple': 'credit-card',
  'cart': 'shopping-cart',
  'airplane': 'plane',
  'gamepad-variant': 'gamepad',
  'dumbbell': 'activity',
  'food-apple': 'shopping-bag',
  'cup': 'coffee',
  'cookie': 'shopping-bag',
  'bike': 'car',
  'train': 'car',
  'bus': 'car',
  'movie': 'play',
  'book': 'book',
  'palette': 'star',
  'basketball': 'activity',
  'yoga': 'activity',
  'pill': 'activity',
  'sofa': 'home',
  'lamp': 'lightbulb',
  'washing-machine': 'settings',
  'tools': 'settings',
  'tablet': 'tablet',
  'shopping': 'shopping-cart',
  'bank': 'building',
  'flower': 'star',
  'tree': 'star',
  'fish': 'star',
};

/**
 * Получает конфигурацию иконки и цвета для категории
 * @param categoryName - название категории
 * @param customIconName - имя кастомной иконки (если есть)
 */
export function getCategoryIconConfig(categoryName: string, customIconName?: string | null): CategoryIconConfig {
  if (!categoryName) {
    return {
      icon: 'folder',
      color: '#9CA3AF',
      backgroundColor: '#F3F4F6',
    };
  }

  // Если есть кастомная иконка, используем её
  if (customIconName) {
    const customIcon = getCustomIconByName(customIconName);
    if (customIcon) {
      // Маппим имя иконки на веб-версию
      const mappedIcon = ICON_MAPPING[customIcon.icon] || customIcon.icon;
      return {
        icon: mappedIcon,
        color: customIcon.color,
        backgroundColor: customIcon.backgroundColor,
      };
    }
  }

  const categoryLower = categoryName.toLowerCase().trim();

  // Явный маппинг Plaid (primary + subcategories)
  const plaidConfig = PLAID_CATEGORY_ICONS[categoryLower];
  if (plaidConfig) {
    return {
      icon: plaidConfig.icon,
      color: plaidConfig.color,
      backgroundColor: plaidConfig.bg,
    };
  }

  // Продукты
  if (categoryLower.includes('продукт')) {
    return {
      icon: 'shopping-cart',
      color: PASTEL_COLORS.products.color,
      backgroundColor: PASTEL_COLORS.products.bg,
    };
  }

  // Парковка
  if (categoryLower.includes('парковк')) {
    return {
      icon: 'car',
      color: '#7B9CF5', // Мягкий пастельный синий (как у транспорта)
      backgroundColor: '#F0F4FF', // Светло-синий фон (как у транспорта)
    };
  }

  // Транспорт
  if (categoryLower.includes('транспорт')) {
    return {
      icon: 'car',
      color: PASTEL_COLORS.transport.color,
      backgroundColor: PASTEL_COLORS.transport.bg,
    };
  }

  // ЖКХ
  if (categoryLower.includes('жкх') || categoryLower.includes('коммунал')) {
    return {
      icon: 'home',
      color: PASTEL_COLORS.utilities.color,
      backgroundColor: PASTEL_COLORS.utilities.bg,
    };
  }

  // Кафе и рестораны
  if (categoryLower.includes('кафе') || categoryLower.includes('ресторан')) {
    return {
      icon: 'coffee',
      color: PASTEL_COLORS.cafe.color,
      backgroundColor: PASTEL_COLORS.cafe.bg,
    };
  }

  // Одежда и обувь
  if (categoryLower.includes('одежд') || categoryLower.includes('обувь')) {
    return {
      icon: 'shopping-bag',
      color: PASTEL_COLORS.clothing.color,
      backgroundColor: PASTEL_COLORS.clothing.bg,
    };
  }

  // Здоровье
  if (categoryLower.includes('здоровье') || categoryLower.includes('аптек') || categoryLower.includes('врач') || categoryLower.includes('медицин')) {
    return {
      icon: 'activity',
      color: PASTEL_COLORS.health.color,
      backgroundColor: PASTEL_COLORS.health.bg,
    };
  }

  // Развлечения
  if (categoryLower.includes('развлечен') || categoryLower.includes('кино') || categoryLower.includes('игр')) {
    return {
      icon: 'gamepad',
      color: PASTEL_COLORS.entertainment.color,
      backgroundColor: PASTEL_COLORS.entertainment.bg,
    };
  }

  // Подписки
  if (categoryLower.includes('подписк')) {
    return {
      icon: 'credit-card',
      color: PASTEL_COLORS.subscriptions.color,
      backgroundColor: PASTEL_COLORS.subscriptions.bg,
    };
  }

  // Подарки
  if (categoryLower.includes('подарк')) {
    return {
      icon: 'gift',
      color: PASTEL_COLORS.gifts.color,
      backgroundColor: PASTEL_COLORS.gifts.bg,
    };
  }

  // Личное
  if (categoryLower.includes('личное') || categoryLower.includes('личн')) {
    return {
      icon: 'user',
      color: PASTEL_COLORS.personal.color,
      backgroundColor: PASTEL_COLORS.personal.bg,
    };
  }

  // Дом
  if (categoryLower.includes('дом') && !categoryLower.includes('жкх')) {
    return {
      icon: 'home',
      color: PASTEL_COLORS.home.color,
      backgroundColor: PASTEL_COLORS.home.bg,
    };
  }

  // Образование
  if (categoryLower.includes('образова') || categoryLower.includes('курс') || categoryLower.includes('учеб')) {
    return {
      icon: 'book',
      color: PASTEL_COLORS.education.color,
      backgroundColor: PASTEL_COLORS.education.bg,
    };
  }

  // Питомцы
  if (categoryLower.includes('питомц') || categoryLower.includes('животн')) {
    return {
      icon: 'star',
      color: PASTEL_COLORS.pets.color,
      backgroundColor: PASTEL_COLORS.pets.bg,
    };
  }

  // Связь
  if (categoryLower.includes('связь') || categoryLower.includes('телефон') || categoryLower.includes('мобильн')) {
    return {
      icon: 'phone',
      color: PASTEL_COLORS.communication.color,
      backgroundColor: PASTEL_COLORS.communication.bg,
    };
  }

  // Финансы
  if (categoryLower.includes('финанс') || categoryLower.includes('кредит') || categoryLower.includes('ипотек') || categoryLower.includes('налог')) {
    return {
      icon: 'wallet',
      color: PASTEL_COLORS.finance.color,
      backgroundColor: PASTEL_COLORS.finance.bg,
    };
  }

  // Путешествия
  if (categoryLower.includes('путешеств') || categoryLower.includes('отпуск') || categoryLower.includes('туризм')) {
    return {
      icon: 'plane',
      color: PASTEL_COLORS.travel.color,
      backgroundColor: PASTEL_COLORS.travel.bg,
    };
  }

  // Дети
  if (categoryLower.includes('дет') || categoryLower.includes('алимент')) {
    return {
      icon: 'user',
      color: PASTEL_COLORS.children.color,
      backgroundColor: PASTEL_COLORS.children.bg,
    };
  }

  // Благотворительность
  if (categoryLower.includes('благотворительн') || categoryLower.includes('пожертвован') || categoryLower.includes('помощь')) {
    return {
      icon: 'heart',
      color: PASTEL_COLORS.charity.color,
      backgroundColor: PASTEL_COLORS.charity.bg,
    };
  }

  // Спорт (если есть отдельная категория)
  if (categoryLower.includes('спорт') || categoryLower.includes('фитнес') || categoryLower.includes('трениров')) {
    return {
      icon: 'activity',
      color: '#6BCB9A',
      backgroundColor: '#F0FDF4',
    };
  }

  // Дефолтная иконка для неизвестных категорий
  return {
    icon: 'folder',
    color: '#9CA3AF',
    backgroundColor: '#F3F4F6',
  };
}

/**
 * Получает только иконку для категории
 * @param categoryName - название категории
 * @param customIconName - имя кастомной иконки (если есть)
 */
export function getCategoryIcon(categoryName: string, customIconName?: string | null): string {
  return getCategoryIconConfig(categoryName, customIconName).icon;
}

/**
 * Получает только цвет для категории
 * @param categoryName - название категории
 * @param customIconName - имя кастомной иконки (если есть)
 */
export function getCategoryColor(categoryName: string, customIconName?: string | null): string {
  return getCategoryIconConfig(categoryName, customIconName).color;
}

/**
 * Получает только цвет фона для категории
 * @param categoryName - название категории
 * @param customIconName - имя кастомной иконки (если есть)
 */
export function getCategoryBackgroundColor(categoryName: string, customIconName?: string | null): string {
  return getCategoryIconConfig(categoryName, customIconName).backgroundColor;
}

